import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { ModalNavigation } from './ModalNavigation';

const TYPE_LABELS = {
  about: 'Profile',
  services: 'Services',
  testimonials: 'Testimonials',
  skills: 'Skills',
  past_projects: 'Past Projects',
  proposal: 'Proposals',
};

const AIKnowledgeViewModal = ({ item, items, currentIndex, onNavigate, onClose }) => {
  const type = item.type || item.metadata?.type;
  const canNavigate = items.length > 1;

  useEffect(() => {
    if (!canNavigate) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
      else if (e.key === 'ArrowRight' && currentIndex < items.length - 1) onNavigate(currentIndex + 1);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, items.length, canNavigate, onNavigate, onClose]);

  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
      <div className="relative bg-white w-full max-w-4xl mx-auto rounded-lg shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">View Entry</h3>
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">
              {TYPE_LABELS[type] || type}
            </span>
          </div>

          {canNavigate && (
            <ModalNavigation
              currentIndex={currentIndex}
              totalCount={items.length}
              onPrevious={() => currentIndex > 0 && onNavigate(currentIndex - 1)}
              onNext={() => currentIndex < items.length - 1 && onNavigate(currentIndex + 1)}
              itemName="Entry"
            />
          )}

          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {type === 'proposal' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Job Title</label>
                <p className="text-sm text-gray-900">{item.metadata?.jobTitle || 'N/A'}</p>
              </div>
              {item.metadata?.jobDescription && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Job Description</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{item.metadata.jobDescription}</p>
                </div>
              )}
              {item.metadata?.coverLetter && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Cover Letter</label>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{item.metadata.coverLetter}</p>
                  </div>
                </div>
              )}
            </>
          )}
          {type === 'skills' && item.metadata?.skill_name && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Skill Name</label>
              <p className="text-sm text-gray-900">{item.metadata.skill_name}</p>
            </div>
          )}
          {type === 'past_projects' && item.metadata?.project_name && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Project Name</label>
              <p className="text-sm text-gray-900">{item.metadata.project_name}</p>
            </div>
          )}
          {['about', 'services', 'testimonials'].includes(type) && item.metadata?.name && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <p className="text-sm text-gray-900">{item.metadata.name}</p>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Content</label>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                {item.text || item.metadata?.text || 'No content'}
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIKnowledgeViewModal;
